import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import { useAuth } from '../hooks/useAuth'
import { api } from '../services/api'

interface DashboardStats {
  totalCustomers: number
  totalLicenses: number
  totalM365Subscriptions: number
  totalM365Quantity: number
  totalAzureSubscriptions: number
  customersWithLicenses: number
  customersWithAzure: number
  customersWithM365: number
  topCustomers: Array<{ Id: string; CompanyName: string; TotalLicenses: number }>
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6']

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { getAccessToken } = useAuth()

  useEffect(() => {
    async function fetchStats() {
      try {
        setLoading(true)
        setError(null)
        const token = await getAccessToken()
        const data = await api.getDashboardStats(token)
        setStats(data)
      } catch (err) {
        console.error('Failed to fetch dashboard stats:', err)
        setError('Failed to load dashboard statistics')
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="px-4 sm:px-0">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <p className="mt-2 text-sm text-gray-500">Loading dashboard...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !stats) {
    return (
      <div className="px-4 sm:px-0">
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{error || 'Failed to load dashboard'}</p>
        </div>
      </div>
    )
  }

  const customerDistribution = [
    { name: 'With M365 Licenses', value: stats.customersWithLicenses },
    { name: 'With Azure', value: stats.customersWithAzure },
  ]

  return (
    <div className="px-4 sm:px-0">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h2>

      {/* Main Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">Total Customers</dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">{stats.totalCustomers}</dd>
            <p className="mt-2 text-xs text-gray-500">With active products</p>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">Total Licenses</dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">{stats.totalLicenses.toLocaleString()}</dd>
            <p className="mt-2 text-xs text-gray-500">M365 license units</p>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">M365 Subscriptions</dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">{stats.totalM365Subscriptions}</dd>
            <p className="mt-2 text-xs text-gray-500">Active subscriptions</p>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">Azure Subscriptions</dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">{stats.totalAzureSubscriptions}</dd>
            <p className="mt-2 text-xs text-gray-500">Active Azure subs</p>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Top Customers Chart */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Top 5 Customers by Licenses</h3>
          <div className="space-y-3">
            {stats.topCustomers.map((customer, index) => {
              const maxLicenses = stats.topCustomers[0]?.TotalLicenses || 1
              const percentage = (customer.TotalLicenses / maxLicenses) * 100
              return (
                <div key={customer.Id} className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-500 w-5">{index + 1}</span>
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/customers/${customer.Id}`}
                      className="text-sm font-medium text-primary-600 hover:text-primary-800 hover:underline truncate block"
                    >
                      {customer.CompanyName}
                    </Link>
                    <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-gray-700 w-16 text-right">
                    {customer.TotalLicenses.toLocaleString()}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        {/* Customer Distribution Pie Chart */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Customer Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={customerDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {customerDistribution.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">Customers with M365 Licenses</dt>
            <dd className="mt-1 text-2xl font-semibold text-gray-900">{stats.customersWithLicenses}</dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">Customers with Azure</dt>
            <dd className="mt-1 text-2xl font-semibold text-gray-900">{stats.customersWithAzure}</dd>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">M365 Subscription Quantity</dt>
            <dd className="mt-1 text-2xl font-semibold text-gray-900">{stats.totalM365Quantity.toLocaleString()}</dd>
          </div>
        </div>
      </div>
    </div>
  )
}
